import { useCallback, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { CheckCircle, Eye, FileText } from "lucide-react";

import { ProcessMessagesButton } from "components/ProcessMessagesButton/ProcessMessagesButton";
import { DataTable } from "components/ui/DataTable/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Pill } from "components/ui/Pill";
import { useMaternityHospitals } from "features/pregnancy/hooks/useMaternityHospitals";
import { useMncmsInboundMessages } from "features/pregnancy/hooks/useMncmsInboundMessages";
import { useMncmsOutboundMessages } from "features/pregnancy/hooks/useMncmsOutboundMessages";
import { abbreviateHospitalName } from "features/pregnancy/utils/hospitalShortName";
import { formatSendingFacility } from "features/pregnancy/utils/inboundSendingFacility";
import { getMncmsOutboundStatus } from "features/pregnancy/utils/mncmsOutboundStatus";

import { MncmsMessageDetail } from "./MncmsMessageDetail";
import style from "./PregnancyMessagesView.module.css";

const SORT_OPTIONS = [
  { field: "newest-first", direction: "", label: "Newest first" },
  { field: "oldest-first", direction: "", label: "Oldest first" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return format(parseISO(value), "dd/MM/yyyy, HH:mm");
  } catch {
    return "-";
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return format(parseISO(value), "dd/MM/yyyy");
  } catch {
    return "-";
  }
};

const MessageLogTable = ({
  title,
  headerAction,
  data,
  isLoading,
  columns,
  getSearchText,
  getSortValue,
  getRowClassName,
  rowActions,
}) => {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "newest-first", direction: "" });

  const rows = useMemo(() => {
    const filtered = (data || []).filter(
      (row) =>
        !search ||
        getSearchText(row).toLowerCase().includes(search.toLowerCase())
    );

    return [...filtered].sort((a, b) => {
      const valueA = getSortValue(a) || "";
      const valueB = getSortValue(b) || "";
      return sort.field === "newest-first"
        ? valueB.localeCompare(valueA)
        : valueA.localeCompare(valueB);
    });
  }, [data, search, sort, getSearchText, getSortValue]);

  return (
    <section className={style.card}>
      <div className={style.cardHeader}>
        <h3 className={style.cardTitle}>{title}</h3>
        {headerAction}
      </div>
      <DataTable
        columns={columns}
        data={rows}
        noScroll
        getRowId={(row) => row.id}
        isLoading={isLoading}
        getRowClassName={getRowClassName}
        rowActions={rowActions}
        actionsColumnWidth="32px"
        emptyTitle="No messages found"
        emptyText="There are no messages to display at this time."
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search",
          maxWidth: "210px",
        }}
        sort={sort}
        sortOptions={SORT_OPTIONS}
        onSortChange={(field, direction) => setSort({ field, direction })}
      />
    </section>
  );
};

const buildInboundColumns = (resolveFacilityLabel) => [
  {
    id: "date",
    header: "Date & Time",
    width: "170px",
    render: (row) => formatDateTime(row.receivedAt),
  },
  /*
   * Shortened, with the full name on hover. At full length these two columns and a long summary
   * pushed the row past the panel, and the view action off the right-hand edge — a GP had to find
   * a horizontal scrollbar to open a message, which Darren did not (Eleanor, Darren).
   */
  {
    id: "hospital",
    header: "Hospital",
    width: "110px",
    render: (row) => abbreviateHospitalName(row.hospitalName) || "-",
    getTooltipText: (row) => row.hospitalName || "-",
  },
  /*
   * Which site actually sent this message, from MSH.4 (KIN-1939). A separate column rather than a
   * replacement for the one above, because they are different facts and the ticket's own case is
   * the one where they disagree: Hospital is resolved from the reflected consultant — who is
   * treating her — while this is where the message came from. When care transfers mid-pregnancy
   * the pregnancy holds one hospital and the messages behind it need not agree, and until now
   * that disagreement was invisible.
   */
  {
    id: "sendingFacility",
    header: "Came from",
    width: "110px",
    render: (row) => abbreviateHospitalName(resolveFacilityLabel(row)) || "-",
    getTooltipText: (row) => resolveFacilityLabel(row) || "-",
  },
  /*
   * Base width 0 so the column is `minmax(0, 1fr)`: it takes what is left and truncates, rather
   * than sizing itself to the longest summary and widening the table to suit.
   */
  {
    id: "summary",
    header: "Summary",
    flexGrow: 1,
    width: "0",
    render: (row) => row.summary || "-",
    getTooltipText: (row) => row.summary || "-",
  },
  /* Holds a name occasionally and a dash the rest of the time, so it takes what it needs and
     leaves the rest to the summary — which is the column a GP actually reads. */
  {
    id: "reviewedBy",
    header: "Reviewed by",
    width: "110px",
    render: (row) => row.signedOffBy || "-",
    getTooltipText: (row) => row.signedOffBy || "-",
  },
  {
    id: "state",
    header: "",
    width: "96px",
    render: (row) => {
      if (row.isCorrection) {
        return (
          <span className={style.correctionFlag}>
            Correction
            <FileText size={16} />
          </span>
        );
      }

      if (row.signedOff) {
        return <CheckCircle size={18} className={style.reviewedIcon} />;
      }

      return null;
    },
  },
];

const outboundColumns = [
  {
    id: "date",
    header: "Date & Time",
    width: "170px",
    render: (row) => formatDateTime(row.sentAt),
  },
  /* Same treatment as the inbound log: the column was narrowed, so the name is shortened to fit
     and the full one is on hover. KIN-2030 records MSH.6 at send time; a message sent before that
     has no name to show and reads "-". */
  {
    id: "hospital",
    header: "Hospital",
    width: "110px",
    render: (row) => abbreviateHospitalName(row.hospitalName) || "-",
    getTooltipText: (row) => row.hospitalName || "-",
  },
  {
    id: "visitDate",
    header: "Visit Date",
    flexGrow: 1,
    render: (row) => formatDate(row.visitDate),
  },
  {
    id: "status",
    header: "Status",
    width: "140px",
    render: (row) => {
      const { label, color, bg, border } = getMncmsOutboundStatus(
        row.displayStatus
      );
      return (
        <Pill color={color} bg={bg} border={border}>
          {label}
        </Pill>
      );
    },
  },
];

export const PregnancyMessagesView = ({ pregnancy, onSendCorrection }) => {
  const pregnancyId = pregnancy?.id;
  const [selected, setSelected] = useState(null);

  const handleSendCorrection = (consultationId) => {
    setSelected(null);
    onSendCorrection?.(consultationId);
  };

  const { data: inboundMessages, isLoading: isInboundLoading } =
    useMncmsInboundMessages(pregnancyId);

  /*
   * The backend stores a sending-facility code it could not resolve as-is and leaves the name
   * null, which on UAT means this column read "ULHG" rather than "UL Hospitals Group". The same
   * list the hospital picker uses resolves it, so the row says a hospital's name instead of its
   * mnemonic — and still falls back to the raw code, because a code we cannot name is worth
   * showing rather than hiding.
   */
  const { data: hospitalOptions } = useMaternityHospitals();
  const resolveFacilityLabel = useCallback(
    (message) => {
      /*
       * Resolved on the code, not on what would otherwise be displayed. The backend's
       * sendingFacilityName is the site mnemonic on UAT — "ULHG", "NMH" — so looking the list up
       * by that finds nothing and the row keeps showing an abbreviation only the hospital uses.
       * The code is what the list is keyed on, and it is the same value the hospital picker
       * stores.
       */
      const byCode = hospitalOptions?.find(
        (option) => option.value === message?.sendingFacilityCode
      );

      return byCode?.label ?? formatSendingFacility(message);
    },
    [hospitalOptions]
  );
  const inboundColumns = useMemo(
    () => buildInboundColumns(resolveFacilityLabel),
    [resolveFacilityLabel]
  );
  const { data: outboundMessages, isLoading: isOutboundLoading } =
    useMncmsOutboundMessages(pregnancyId);

  const processButton = (
    <ProcessMessagesButton
      title="Process maternity messages"
      successMessage="Maternity messages processed successfully!"
    />
  );

  return (
    <div className={style.wrapper}>
      <MessageLogTable
        title="Inbound Messages"
        headerAction={processButton}
        data={inboundMessages}
        isLoading={isInboundLoading}
        columns={inboundColumns}
        getSearchText={(row) =>
          `${row.hospitalName ?? ""} ${row.summary ?? ""}`
        }
        getSortValue={(row) => row.receivedAt}
        rowActions={[
          {
            label: "View",
            icon: Eye,
            primary: true,
            onClick: (row) =>
              setSelected({ message: row, direction: "inbound" }),
          },
        ]}
      />

      <MessageLogTable
        title="Outbound Messages"
        headerAction={processButton}
        data={outboundMessages}
        isLoading={isOutboundLoading}
        columns={outboundColumns}
        getSearchText={(row) => `${row.hospitalName ?? ""}`}
        getSortValue={(row) => row.sentAt}
        rowActions={[
          {
            label: "View",
            icon: Eye,
            primary: true,
            onClick: (row) =>
              setSelected({ message: row, direction: "outbound" }),
          },
        ]}
      />

      {!!selected && (
        <Dialog
          open
          title="Message preview"
          onClose={() => setSelected(null)}
          width={1100}
        >
          <MncmsMessageDetail
            message={selected.message}
            direction={selected.direction}
            onSendCorrection={handleSendCorrection}
          />
        </Dialog>
      )}
    </div>
  );
};
